import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, AlertTriangle, Plus } from 'lucide-react';
import { format } from 'date-fns';

export default function SyncTab({
  missingReservations,
  syncLoading,
  lastSyncCheck,
  onSync,
  onCreateFromMissing
}) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Synchronisation des calendriers
          </CardTitle>
          <Button onClick={onSync} disabled={syncLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
            {syncLoading ? 'Vérification...' : 'Vérifier maintenant'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground mb-4">
          Comparez vos calendriers externes (Booking, Airbnb) avec vos réservations locales.
          {lastSyncCheck && (
            <span className="ml-2 text-sm">
              Dernière vérification: {format(new Date(lastSyncCheck), 'dd/MM/yyyy HH:mm')}
            </span>
          )}
        </p>

        {missingReservations.length === 0 && !syncLoading && (
          <div className="text-center py-8 text-muted-foreground border rounded-lg bg-gray-50">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Cliquez sur "Vérifier maintenant" pour détecter les réservations manquantes</p>
          </div>
        )}

        {missingReservations.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-600 mb-4">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                {missingReservations.length} réservation{missingReservations.length > 1 ? 's' : ''} détectée{missingReservations.length > 1 ? 's' : ''} dans vos calendriers externes
              </span>
            </div>
            
            {missingReservations.map((missing, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg bg-amber-50">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline">{missing.source}</Badge>
                  </div>
                  <p className="font-medium">
                    {format(new Date(missing.check_in), 'dd/MM/yyyy')} → {format(new Date(missing.check_out), 'dd/MM/yyyy')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {Math.ceil((new Date(missing.check_out) - new Date(missing.check_in)) / (1000 * 60 * 60 * 24))} nuits
                  </p>
                </div>
                <Button onClick={() => onCreateFromMissing(missing)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer réservation
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Configurez vos flux iCal dans{' '}
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/settings?tab=ical')}>
              Paramètres → Calendrier iCal
            </Button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
